import { request } from './request';
import TealiumEvents, { UnsetTealiumMerchantDetails } from 'src/helpers/tealium-events';
import { reloadEnvironmentConfig } from 'src/Environment';
import { AppCacheKeys } from 'src/types';

const loggedInKey = 'smme_portal.loggedIn';
const loginType = 'smme_portal.logInType';
const userIdKey = 'smme_portal.userId';
const merchantIdKey = 'smme_portal.userName';
export const dealerId = 'sales_org';
export const appDynamicsUserIdKey = 'smme_portal.appDynamicsUserId';
const sessionStartTimeKey = 'smme_portal.sessionStartTime';
const otpKey = 'smme_portal.otpPassword';
const changeFailureMessage = 'Failed to set password, please try again...';
const remotePayment = 'broker';
const agentJourney = 'agent_journey';

export function getUserId() {
  return sessionStorage.getItem(userIdKey);
}

export function getAgentJourney() {
  return sessionStorage.getItem(agentJourney);
}

export function getDeviceActivationJourney() {
  return sessionStorage.getItem(AppCacheKeys.deviceActivation);
}

export function getRestartApplication() {
  return sessionStorage.getItem(AppCacheKeys.RestartApp) === 'true';
}

export function getRemotePaymentOption() {
  const broker = JSON.parse(sessionStorage.getItem(remotePayment) || '{}');
  return broker?.remotePaymentsEnabled;
}

export function getMerchantId() {
  return sessionStorage.getItem(merchantIdKey) || '';
}

export function isLoggedIn() {
  return (
    sessionStorage.getItem(loggedInKey) === 'true' &&
    sessionStorage.getItem(loginType) !== 'CHANGE_PASSWORD'
  );
}
export function getDealerId() {
  return sessionStorage.getItem(dealerId) || '';
}

export function getAgentId(isAgentOr3rdParty) {
  if (!isAgentOr3rdParty) {
    sessionStorage.setItem(merchantIdKey, '');
    return '';
  }

  return sessionStorage.getItem(merchantIdKey) || '';
}

export function isLoggedInChangePassword() {
  return (
    sessionStorage.getItem(loggedInKey) === 'true' &&
    sessionStorage.getItem(loginType) === 'CHANGE_PASSWORD'
  );
}

export function setOtpForSession(otpValue) {
  sessionStorage.setItem(otpKey, otpValue);
}

export function getOtpForSession() {
  return sessionStorage.getItem(otpKey);
}

export function setSessionStartTime(sessionStartTime) {
  sessionStorage.setItem(sessionStartTimeKey, sessionStartTime);
}

export function getSessionStartTime() {
  return sessionStorage.getItem(sessionStartTimeKey);
}

export async function authenticate({ merchantId, password, showToastOnError = true }) {
  const res = await request(
    'login',
    {
      fetchOptions: {
        method: 'POST',
        body: JSON.stringify({ channel: 'WEB', username: merchantId, password: btoa(password) }),
      },
    },
    null,
    null,
    showToastOnError,
    true,
  );
  if (res?.status === 0) {
    return {
      ok: false,
      message: res.errorMessage,
      isTechnical: true,
      error: res?.errorMessage || 'error',
    };
  }
  let message = '';
  if (res) {
    const body = (await res.result) || {};
    if (res.status === 200) {
      sessionStorage.setItem(loggedInKey, true);
      sessionStorage.setItem(loginType, body.resultMessage);
      sessionStorage.setItem(userIdKey, body.userId);
      sessionStorage.setItem(merchantIdKey, merchantId.trimEnd());
      setSessionStartTime(new Date());
    } else if (body.errors) {
      message = body.errors[0].msg;
    } else {
      message = body.message;
    }
    return {
      ok: res.status === 200,
      message: message,
      isTechnical: res.status >= 500,
      firstTimeLogin: body.resultMessage === 'CHANGE_PASSWORD',
      error: res.status === 200 ? null : message || 'error',
    };
  }
}

export async function changePassword(
  { oldPassword, newPassword },
  abortController,
  showToastOnError = true,
) {
  const userId = getUserId();
  const res = await request(
    'change-password',
    {
      fetchOptions: {
        method: 'POST',
        body: JSON.stringify({
          userId: userId,
          oldPassword: btoa(oldPassword),
          newPassword: btoa(newPassword),
        }),
        signal: abortController?.signal,
      },
    },
    null,
    null,
    showToastOnError,
    true,
  );
  let message = '';
  if (res) {
    const body = (await res.result) || {};
    if (res.status === 200) {
      return body;
    } else if (body.errors) {
      message = body.errors[0].msg || changeFailureMessage;
    } else {
      message = body.errorMessage || changeFailureMessage;
    }
    return {
      ok: res.status === 200,
      message: message,
      isTechnical: res.status >= 500,
      error: res.status === 200 ? null : message || 'error',
    };
  }
}

export async function resetPassword(
  merchantId,
  abortController,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  suppressTealiumEvent = false,
  showToastOnError = true,
) {
  const res = await request(
    'reset-password',
    {
      fetchOptions: {
        method: 'POST',
        body: JSON.stringify({
          userName: merchantId,
        }),
        signal: abortController?.signal,
      },
    },
    null,
    null,
    showToastOnError,
    true,
  );
  let message = '';
  if (res) {
    const body = (await res.result) || {};

    if (res.status === 200) {
      sessionStorage.setItem(userIdKey, body.userId);
      sessionStorage.setItem(merchantIdKey, merchantId);
      return body;
    } else if (body.errors) {
      message = body.errors[0].msg || 'Failed to reset password';
    } else {
      message = body.errorMessage || 'Failed to reset password';
    }
    return {
      ok: res.status === 200,
      message: message,
      isTechnical: res.status >= 500,
      error: res.status === 200 ? null : message || 'error',
    };
  }
}

export async function logout(handleErrorResponse = null, suppressTealiumEvent = false) {
  TealiumEvents.logout();
  UnsetTealiumMerchantDetails();
  const userId = sessionStorage.getItem(userIdKey);
  const res = await request(
    'logoff',
    {
      fetchOptions: {
        method: 'POST',
        body: JSON.stringify({ userId: userId }),
      },
    },
    null,
    null,
    false,
    suppressTealiumEvent,
  );

  sessionStorage.clear();
  localStorage.clear();
  reloadEnvironmentConfig();

  if (res?.status === 200) {
    return res.result;
  } else if (handleErrorResponse) {
    handleErrorResponse(
      'error response from the logoff POST endpoint',
      true,
      suppressTealiumEvent,
      res,
    );
  }
}

export async function expiredSessionLogout() {
  TealiumEvents.logout();
  UnsetTealiumMerchantDetails();
  sessionStorage.clear();
}
